import {
  EHeadingStyleVariant,
  EIconButtonVariant,
  ETextStyleVariant,
  GeneralCaretLeftIcon,
  GeneralCaretRightIcon,
  Grid,
  GridItem,
  Heading,
  IconButton,
  Text,
} from '@outdoorsyco/bonfire';
import Image from 'next/image';
import React, { useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Slider, { Settings as SliderSettings } from 'react-slick';

import CampgroundHost from '@/assets/become-a-host/campground/host.jpg';
import CampgroundHostBG from '@/assets/become-a-host/campground/hostBG.jpg';
import Host1 from '@/assets/become-a-host/rv/host1_chip_conrad.jpg';
import HostRental1 from '@/assets/become-a-host/rv/Host1_chip_conrad_rental.jpg';
import Host2 from '@/assets/become-a-host/rv/host2_tina_howitt.jpg';
import HostRental2 from '@/assets/become-a-host/rv/host2_tina_howitt_rental.jpg';
import Host3 from '@/assets/become-a-host/rv/host3_jess_van_zijill.jpg';
import HostRental3 from '@/assets/become-a-host/rv/host3_jess_van_zijill_rental.jpg';
import Host4 from '@/assets/become-a-host/rv/host4_jess_chris.jpg';
import HostRental4 from '@/assets/become-a-host/rv/host4_jess_chris_rental.jpg';
import { ERentalType } from '@/services/analytics/types';

import styles from './BecomeAHost.module.css';
import { useHostingType } from './useHostingType';

export const Testimonials = () => {
  const { rentalType, isRentalRV } = useHostingType();

  const sliderRef = useRef<Slider>(null);
  const intl = useIntl();
  const hostingRentalImageALT = intl.formatMessage({
    defaultMessage: 'Hosting rental image',
    id: 'Mip0wU',
  });
  const hostImageALT = intl.formatMessage({ defaultMessage: 'Host image', id: 'rBeOiZ' });

  const TestimonialData = {
    [ERentalType.RV]: [
      {
        hostRentalImage: HostRental1,
        hostImage: Host1,
        name: 'Chip Conrad',
        hostSinceYear: '2020',
        quote: (
          <>
            <Heading variant={EHeadingStyleVariant.H5} className="mb-4">
              <FormattedMessage defaultMessage="GREAT CUSTOMER SERVICE." id="uTGh66" />
            </Heading>
            <Heading variant={EHeadingStyleVariant.H5}>
              <FormattedMessage
                defaultMessage="The game changer is how they work with my clients. My clients and I can rest assured that we will be taken care of in a fast friendly manner 24/7!"
                id="H5xOTh"
              />
              <span className="relative ml-1 font-serif text-bark-30 text-900 leading-4 top-[8px]">
                &rdquo;
              </span>
            </Heading>
          </>
        ),
        signatureHostingMoves: (
          <>
            <Text
              variant={ETextStyleVariant.LegalBold}
              className="pb-2 pl-4 border-l-2 border-bark-30">
              <FormattedMessage
                defaultMessage="I walk my renter around the rig showing them all the dings and dents that were there before them."
                id="29PPP3"
              />
            </Text>
            <Text variant={ETextStyleVariant.LegalBold} className="pl-4 border-l-2 border-bark-30">
              <FormattedMessage
                defaultMessage="I say ‘Please bring my van back with the same or less amount of dents."
                id="bFf5oy"
              />
            </Text>
          </>
        ),
      },

      {
        hostRentalImage: HostRental2,
        hostImage: Host2,
        name: 'Tina Howitt',
        hostSinceYear: '2022',
        quote: (
          <Heading variant={EHeadingStyleVariant.H5}>
            <FormattedMessage
              defaultMessage="It’s like having a personal assistant for our small business. They bring us new clients and provide our guests with 24/7 customer service and peace of mind."
              id="JY5i0y"
            />
            <span className="relative ml-1 font-serif text-bark-30 text-900 leading-4 top-[8px]">
              &rdquo;
            </span>
          </Heading>
        ),
        signatureHostingMoves: (
          <>
            <Text
              variant={ETextStyleVariant.LegalBold}
              className="pb-2 pl-4 border-l-2 border-bark-30">
              <FormattedMessage
                defaultMessage="We customize the layout to meet each guests’ unique needs."
                id="+KR40v"
              />
            </Text>
            <Text variant={ETextStyleVariant.LegalBold} className="pl-4 border-l-2 border-bark-30">
              <FormattedMessage
                defaultMessage="We provide a complimentary Pooch Package with camper-friendly dog bowls, treats, cable tie-out and stake, dog seatbelt or kennel, and a microfiber towel for muddy paws."
                id="W6i3zG"
              />
            </Text>
          </>
        ),
      },

      {
        hostRentalImage: HostRental3,
        hostImage: Host3,
        name: 'Jessica Van Zijill',
        hostSinceYear: '2018',
        quote: (
          <Heading variant={EHeadingStyleVariant.H5}>
            <FormattedMessage
              defaultMessage="I host with Outdoorsy because it helps others create an unforgettable adventure and share in the beauty of the outdoors."
              id="T3kaLZ"
            />
            <span className="relative ml-1 font-serif text-bark-30 text-900 leading-4 top-[8px]">
              &rdquo;
            </span>
          </Heading>
        ),
        signatureHostingMoves: (
          <>
            <Text
              variant={ETextStyleVariant.LegalBold}
              className="pb-2 pl-4 border-l-2 border-bark-30">
              <FormattedMessage
                defaultMessage="Guide books & adventure novels included with every rental."
                id="iQWaqH"
              />
            </Text>
            <Text variant={ETextStyleVariant.LegalBold} className="pl-4 border-l-2 border-bark-30">
              <FormattedMessage
                defaultMessage="Locally made treats for your furry best friend 🐶."
                id="b1rAhC"
              />
            </Text>
          </>
        ),
      },

      {
        hostRentalImage: HostRental4,
        hostImage: Host4,
        name: 'Jess and Chris',
        hostSinceYear: '2023',
        quote: (
          <Heading variant={EHeadingStyleVariant.H5}>
            <FormattedMessage
              defaultMessage="Outdoorsy was the perfect platform to get our name out, and we felt very comfortable with the coverage and support Outdoorsy had to offer."
              id="Gha5YH"
            />
            <span className="relative ml-1 font-serif text-bark-30 text-900 leading-4 top-[8px]">
              &rdquo;
            </span>
          </Heading>
        ),
        signatureHostingMoves: (
          <Text variant={ETextStyleVariant.LegalBold} className="pl-4 border-l-2 border-bark-30">
            <FormattedMessage
              defaultMessage="Provide a Polaroid camera and journal for families to have fun taking pictures, while making memories to keep or document for other renters to see."
              id="szVoQ5"
            />
          </Text>
        ),
      },
    ],
    [ERentalType.STAY]: null,
    [ERentalType.CAMPGROUND]: [
      {
        hostRentalImage: CampgroundHostBG,
        hostImage: CampgroundHost,
        name: 'Susan B',
        hostSinceYear: '2017',
        quote: (
          <>
            <Heading variant={EHeadingStyleVariant.H3}>
              <FormattedMessage
                defaultMessage="Sharing my love of wide-open spaces and campfire stories is why I host."
                id="y81wJZ"
              />
              <span className="relative ml-1 font-serif text-bark-30 text-900 leading-4 top-[8px]">
                &rdquo;
              </span>
            </Heading>
          </>
        ),
        signatureHostingMoves: (
          <>
            <Text
              variant={ETextStyleVariant.LegalBold}
              className="pb-2 pl-4 border-l-2 border-bark-30">
              <FormattedMessage
                defaultMessage="Sends guests a link to download her favorite star-gazing app before their trip begins."
                id="3/iVCv"
              />
            </Text>
            <Text variant={ETextStyleVariant.LegalBold} className="pl-4 border-l-2 border-bark-30">
              <FormattedMessage
                defaultMessage="Includes a firewood bundle and a s’mores kit with every rental."
                id="htpoZr"
              />
            </Text>
          </>
        ),
      },
    ],
  };

  const testimonials = TestimonialData[rentalType];
  if (!testimonials) return null;

  const settings: Partial<SliderSettings> = {
    dots: false,
    infinite: testimonials.length > 1,
    arrows: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  return (
    <section className="relative mx-auto max-w-xxl">
      {isRentalRV && (
        <div className={`${styles.spacing} text-center`}>
          <Text variant={ETextStyleVariant.LegalRegular} className="mb-4 uppercase">
            <FormattedMessage defaultMessage="Made for memories" id="iLv9AE" />
          </Text>
          <Heading variant={EHeadingStyleVariant.H3}>
            <FormattedMessage defaultMessage="We say hosting," id="8Thr/q" />
          </Heading>
          <Heading variant={EHeadingStyleVariant.H3Alt}>
            <FormattedMessage defaultMessage="They say..." id="tD9+U9" />
          </Heading>
        </div>
      )}
      <Slider {...settings} ref={sliderRef}>
        {testimonials.map(
          ({ quote, hostImage, hostRentalImage, name, hostSinceYear, signatureHostingMoves }) => (
            <div key={name}>
              <Grid>
                <GridItem xs={12} sm={5} className="relative">
                  <Image
                    src={hostRentalImage}
                    alt={hostingRentalImageALT}
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center"
                  />
                  <div className="relative w-full px-20 text-center md:my-32 md:-right-20 md:text-end md:px-0 translate-y-40 sm:translate-y-60 md:translate-y-0">
                    <Image
                      src={hostImage}
                      alt={hostImageALT}
                      width={350}
                      height={506}
                      objectFit="cover"
                      className="rounded-2xl"
                    />
                  </div>
                </GridItem>

                <GridItem
                  sm
                  className="flex flex-col justify-center px-4 py-8 mt-40 sm:mt-60 md:ml-12 sm:px-8 md:px-24 md:mt-0">
                  <Heading
                    variant={EHeadingStyleVariant.H1}
                    className="pt-4 font-serif text-bark-30 text-9xl leading-4">
                    &ldquo;
                  </Heading>
                  {quote}

                  <Text variant={ETextStyleVariant.SmallBold} className="mt-6 mb-4 uppercase">
                    <FormattedMessage defaultMessage="SIGNATURE HOSTING MOVES" id="al+y0Z" />
                  </Text>
                  {signatureHostingMoves}

                  <div className="flex items-center justify-between mt-12 gap-8">
                    <Text variant={ETextStyleVariant.LegalRegular}>
                      <FormattedMessage
                        defaultMessage="{name}, Outdoorsy host since {year}"
                        id="ltiB71"
                        values={{ name, year: hostSinceYear }}
                      />
                    </Text>
                    {testimonials.length > 1 && (
                      <div className="flex justify-center gap-3 md:justify-end">
                        <IconButton
                          icon={GeneralCaretLeftIcon}
                          onClick={() => sliderRef.current?.slickPrev()}
                          size="small"
                          variant={EIconButtonVariant.Tertiary}
                        />
                        <IconButton
                          icon={GeneralCaretRightIcon}
                          onClick={() => sliderRef.current?.slickNext()}
                          size="small"
                          variant={EIconButtonVariant.Tertiary}
                        />
                      </div>
                    )}
                  </div>
                </GridItem>
              </Grid>
            </div>
          ),
        )}
      </Slider>
    </section>
  );
};
